import React, { useEffect, useRef } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import FadeInSection from './FadeInSection';
import scrollToTop from '../utils/scrollToTop';
import { trackEvent } from '../utils/analytics';
import { INSIGHTS } from '../constants/insights-data';
import '../styles/InsightDetail.scss';

const READ_MILESTONES = [25, 50, 75, 100];

/**
 * Reports how far down an article the reader actually got.
 *
 * page_view alone can't distinguish someone who bounced off the headline from someone who
 * read to the end, which is the only thing that says whether an article is working. Each
 * milestone fires at most once per article.
 */
function useReadDepth(ref, article) {
  useEffect(() => {
    const el = ref.current;
    if (!el || !article) return;

    const reached = new Set();

    const onScroll = () => {
      const { top, height } = el.getBoundingClientRect();
      // How much of the article has scrolled past the bottom of the viewport.
      const scrolled = window.innerHeight - top;
      if (height <= 0) return;
      const pct = Math.min(100, Math.max(0, (scrolled / height) * 100));

      for (const m of READ_MILESTONES) {
        if (pct >= m && !reached.has(m)) {
          reached.add(m);
          trackEvent('article_read_depth', {
            percent: m,
            article_slug: article.slug,
            article_title: article.title,
            article_category: article.category,
          });
        }
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // a short article may already be fully visible on load
    return () => window.removeEventListener('scroll', onScroll);
  }, [ref, article]);
}

// Normalize either body shape into { lede, blocks }.
function normalize(article) {
  if (Array.isArray(article.body)) {
    return { lede: article.lede || '', blocks: article.body };
  }
  const parts = (article.body || '')
    .trim()
    .split(/\n\n+/)
    .map(s => s.trim())
    .filter(Boolean);
  const blocks = parts.map(t => {
    // Single short line with no terminal punctuation = section heading.
    // A trailing '?' is allowed, since question-form headings are common
    // ("Is Now the Right Time to Start Planning?").
    if (!t.includes('\n') && t.length < 90 && !/[.!,;]$/.test(t)) {
      return { type: 'h', text: t };
    }
    return { type: 'p', text: t };
  });
  let lede = article.lede || '';
  if (!lede && blocks.length && blocks[0].type === 'p') {
    lede = blocks.shift().text;
  }
  return { lede, blocks };
}

function InsightDetail() {
  const { slug } = useParams();
  const article = INSIGHTS.find(a => a.slug === slug);
  const bodyRef = useRef(null);

  useEffect(() => {
    scrollToTop();
  }, [slug]);

  useEffect(() => {
    if (!article) return;
    trackEvent('article_view', {
      article_slug: article.slug,
      article_title: article.title,
      article_category: article.category,
    });
  }, [article]);

  useReadDepth(bodyRef, article);

  if (!article) return <Navigate to="/insights" replace />;

  const { lede, blocks } = normalize(article);
  const more = INSIGHTS.filter(a => a.id !== article.id).slice(0, 3);

  return (
    <>
      <Helmet>
        <title>{article.title} | Lily Lai Real Estate</title>
        <meta name="description" content={article.excerpt} />
        <link rel="canonical" href={`https://www.lilylaisf.com/insights/${article.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://www.lilylaisf.com/insights/${article.slug}`} />
        <meta property="og:title" content={`${article.title} | Lily Lai Real Estate`} />
        <meta property="og:description" content={article.excerpt} />
        {article.image && <meta property="og:image" content={article.image.startsWith('http') ? article.image : `https://www.lilylaisf.com${article.image}`} />}
      </Helmet>

      <div className="article-page">

        {/* Header */}
        <div className="article-head-wrap">
          <Link to="/insights" className="article-back" onClick={() => trackEvent('article_back_click', { article_slug: article.slug })}>← All Insights</Link>
          <FadeInSection>
            <header className="article-head">
              <p className="eyebrow">{article.category} · {article.date}</p>
              <h1 className="article-title">{article.title}</h1>
              <p className="article-byline">By Lily Lai</p>
            </header>
          </FadeInSection>
        </div>

        {/* Hero image */}
        {article.image ? (
          <FadeInSection>
            <div className="article-hero-wrap">
              <div
                className="article-hero"
                style={{ backgroundImage: `url('${article.image}')` }}
                role="img"
                aria-label={article.title}
              />
            </div>
          </FadeInSection>
        ) : (
          <FadeInSection>
            <div className="article-hero-wrap">
              <div className="article-hero article-hero-placeholder">
                <span>Market Notes</span>
              </div>
            </div>
          </FadeInSection>
        )}

        {/* Body */}
        <FadeInSection>
          <article className="article-body" ref={bodyRef}>
            {lede && <p className="article-lede">{lede}</p>}

            {blocks.map((block, i) => {
              if (block.type === 'h') return <h2 key={i} className="article-h2">{block.text}</h2>;
              if (block.type === 'q') return <blockquote key={i} className="article-quote">{block.text}</blockquote>;
              if (block.type === 'image') return (
                <img key={i} className="article-inline-image" src={block.src} alt={block.alt} loading="lazy" />
              );
              if (block.type === 'gallery') return (
                <div key={i} className="article-gallery">
                  {block.title && <h2 className="article-h2 article-gallery-title">{block.title}</h2>}
                  <div className="article-gallery-grid">
                    {block.items.map((it, j) => (
                      <img
                        key={j}
                        className="article-gallery-card"
                        src={it.image}
                        alt={it.alt}
                        loading="lazy"
                        width="500"
                        height="500"
                      />
                    ))}
                  </div>
                </div>
              );
              return <p key={i} className="article-p">{block.text}</p>;
            })}

            {article.faq && article.faq.length > 0 && (
              <div className="article-faq">
                <p className="eyebrow article-faq-eyebrow">Frequently Asked Questions</p>
                {article.faq.map(({ q, a }) => (
                  <div className="article-faq-item" key={q}>
                    <h3 className="article-faq-q">{q}</h3>
                    <p className="article-faq-a">{a}</p>
                  </div>
                ))}
              </div>
            )}

            {article.sources && article.sources.length > 0 && (
              <div className="article-sources">
                <p className="eyebrow article-sources-eyebrow">Sources &amp; Additional Reading</p>
                <ul>
                  {article.sources.map(({ label, url }) => (
                    <li key={url}>
                      <a href={url} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('article_source_click', { source_label: label, source_url: url, article_slug: article.slug })}>{label}</a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Contact CTA */}
            <div className="article-cta">
              <div>
                <p className="eyebrow" style={{ margin: '0 0 8px' }}>Have Questions?</p>
                <p className="article-cta-line">I'm curious to hear your story.</p>
              </div>
              <Link to="/contact" className="article-cta-btn" onClick={() => trackEvent('article_cta_click', { article_slug: article.slug })}>Get In Touch</Link>
            </div>
          </article>
        </FadeInSection>

        {/* Continue reading */}
        <section className="article-more">
          <div className="article-more-inner">
            <div className="article-more-head">
              <h2 className="article-more-title">Continue Reading</h2>
              <Link to="/insights" className="article-more-all">All Insights <span className="arrow">→</span></Link>
            </div>
            <div className="article-more-grid">
              {more.map(a => (
                <Link key={a.id} to={`/insights/${a.slug}`} className="article-more-card" onClick={() => { trackEvent('article_related_click', { from_slug: article.slug, to_slug: a.slug, to_title: a.title }); scrollToTop(); }}>
                  <div className="article-more-media">
                    {a.image ? (
                      <div className="article-more-media-inner" style={{ backgroundImage: `url('${a.image}')` }} />
                    ) : (
                      <div className="article-more-media-inner article-more-media-placeholder">
                        <span>Market Notes</span>
                      </div>
                    )}
                  </div>
                  <div className="article-more-body">
                    <span className="article-more-category">{a.category}</span>
                    <h3 className="article-more-card-title">{a.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

      </div>
    </>
  );
}

export default InsightDetail;
